import { Dispatch, SetStateAction, useEffect } from 'react'
import { dateToString, parseToDate } from 'lib/helper'
import { subMinutes } from 'date-fns'
import { ConfirmStock, DeliveryStock, StockType } from 'types/stock'
import { SpecialHolidayType } from 'types/calendar'
import { BreadType } from 'types/bread'
import isSameDay from 'date-fns/isSameDay'
import { FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
import { StoreDeliveryBreadType } from 'types/storeDeliveryBread'

const useConfirmStocks = (
  breads: BreadType[],
  selectedDates: Date[],
  setConfirmStocks: Dispatch<SetStateAction<ConfirmStock[]>>,
  specialHolidays: SpecialHolidayType[],
  stocks: StockType[],
  storeDeliveryBreads: StoreDeliveryBreadType[],
  storeName: string,
  storeRef: FirebaseDocRefType
) => {
  return useEffect(() => {
    const confirmStocks: ConfirmStock[] = []

    for (let i = 0; i < breads.length; i = (i + 1) | 0) {
      const bread = breads[i]
      if (!bread.bread_ref) continue
      const stocksOfBread = stocks.filter((stock) => bread.bread_ref?.id === stock.bread_ref.id)
      const storeDeliveryBread = storeDeliveryBreads.find(
        (storeDeliveryBread) => storeDeliveryBread.bread_ref?.id === bread.bread_ref?.id
      )
      if (!storeDeliveryBread) continue
      const addedStocks: StockType[] = []
      const deletedStocks: StockType[] = []
      const updatedStocks: StockType[] = []
      // 選択された日付から新規登録される在庫を判定する
      for (let j = 0; j < selectedDates.length; j = (j + 1) | 0) {
        const selectedDate = selectedDates[j]
        const date = dateToString(selectedDate)
        const stock = stocksOfBread.find((stock) => isSameDay(parseToDate(stock.date), selectedDate))
        // お届け可能日が設定済であればスキップ
        if (stock) continue
        const {
          maximum_order,
          package_size,
          delivery_company,
          frozen_delivery_type,
          is_frozen_delivery,
          hours_to_deadline,
          how_to_eat,
          how_to_store,
          choices,
          options,
          ref,
        } = storeDeliveryBread
        // 受渡解禁時刻ごとにstockを追加
        const deadline = FirebaseTimestamp.fromDate(subMinutes(selectedDate, hours_to_deadline * 60))
        const stockRef = storeRef.collection('store_stocks').doc(`stock_delivery_${ref?.id}-${date}_00:00`)
        // 受渡解禁時刻ごとにstockを追加する
        const stockValue: DeliveryStock = {
          bread_ref: bread.bread_ref,
          bread_name: bread.name,
          date: date,
          choices,
          deadline: deadline,
          delivery_company,
          frozen_delivery_type,
          how_to_eat,
          how_to_store,
          image_url: bread.image_urls[0] || '',
          is_frozen_delivery,
          last_notified_time: null,
          maximum_order: 1,
          note: '',
          options,
          package_size,
          pickupable_time: '00:00',
          price: bread.price,
          stock: maximum_order,
          stock_ref: stockRef,
          store_name: storeName,
          store_ref: storeRef,
          type: 'delivery',
        }
        addedStocks.push(stockValue)
      }
      // 既存の在庫データを更新 or 削除
      for (let j = 0; j < stocksOfBread.length; j = (j + 1) | 0) {
        const stock = stocksOfBread[j]
        // 選択された日付に存在するものは上書き
        if (selectedDates.some((selectedDate) => isSameDay(selectedDate, parseToDate(stock.date)))) {
          const {
            maximum_order,
            package_size,
            delivery_company,
            frozen_delivery_type,
            is_frozen_delivery,
            hours_to_deadline,
            how_to_eat,
            how_to_store,
            choices,
            options,
            ref,
          } = storeDeliveryBread
          // 受渡解禁時刻ごとにstockを追加
          const deadline = FirebaseTimestamp.fromDate(subMinutes(parseToDate(stock.date), hours_to_deadline * 60))
          const stockRef = storeRef.collection('store_stocks').doc(`stock_delivery_${ref?.id}-${stock.date}_00:00`)
          const stockValue: DeliveryStock = {
            bread_ref: bread.bread_ref,
            bread_name: bread.name,
            date: stock.date,
            choices,
            deadline: deadline,
            delivery_company,
            frozen_delivery_type,
            how_to_eat,
            how_to_store,
            image_url: bread.image_urls[0] || '',
            is_frozen_delivery,
            last_notified_time: null,
            maximum_order: 1,
            note: '',
            options,
            package_size,
            pickupable_time: '00:00',
            price: bread.price,
            stock: maximum_order,
            stock_ref: stockRef,
            store_name: storeName,
            store_ref: storeRef,
            type: 'delivery',
          }
          updatedStocks.push(stockValue)
        } else {
          // 選択された日付に存在しなければ削除
          deletedStocks.push(stock)
        }
      }

      confirmStocks.push({
        addedStocks,
        bread,
        deletedStocks,
        updatedStocks,
      })
    }
    setConfirmStocks([...confirmStocks])
  }, [breads, selectedDates, setConfirmStocks, specialHolidays, stocks, storeDeliveryBreads, storeName, storeRef])
}

export default useConfirmStocks
